import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Sparkles, Shield, Clock, Crown, Check } from "lucide-react";

export default function HomePage() {
  const { isAuthenticated, isPremium } = useAuth();

  const services = [
    {
      id: "interior",
      title: "Innenreinigung",
      description: "Komplette Reinigung aller Innenoberflächen",
      price: "ab 89€",
      image: "https://images.unsplash.com/photo-1733534816908-650eaf9271f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: "exterior",
      title: "Außenreinigung",
      description: "Professionelle Wäsche mit Schutzversiegelung",
      price: "ab 69€",
      image: "https://images.unsplash.com/photo-1753030505007-98ceef668415?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
    {
      id: "complete",
      title: "Komplettaufbereitung",
      description: "Rundum-Pflege für Ihr Fahrzeug",
      price: "ab 249€",
      image: "https://images.unsplash.com/photo-1632823469850-2f77dd9c7f93?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    },
  ];

  const benefits = [
    {
      icon: Sparkles,
      title: "Premium-Qualität",
      description: "Nur hochwertige Produkte und modernste Techniken",
    },
    {
      icon: Shield,
      title: "Garantierte Zufriedenheit",
      description: "Nicht zufrieden? Wir bessern kostenlos nach",
    },
    {
      icon: Clock,
      title: "Flexibel & Schnell",
      description: "Online buchen und Zeit sparen",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1711523164285-71e1dbb948ca?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-in font-['Chivo']">
              Perfektion für
              <span className="text-cyan-400"> Ihr Fahrzeug</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-300 mb-8 animate-fade-in-delay-1">
              Professionelle Fahrzeugaufbereitung mit höchsten Ansprüchen. 
              Erleben Sie den Unterschied – jetzt Termin vereinbaren.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-delay-2">
              <Link to="/booking">
                <Button
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow w-full sm:w-auto"
                  data-testid="hero-booking-btn"
                >
                  Termin buchen
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-zinc-600 text-white hover:bg-zinc-800 w-full sm:w-auto"
                  data-testid="hero-services-btn"
                >
                  Services entdecken
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="services-preview">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 font-['Chivo']">
              Unsere Services
            </h2>
            <p className="text-zinc-400 max-w-2xl">
              Wählen Sie aus unserem umfangreichen Angebot an professionellen Aufbereitungsleistungen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to="/services"
                className={`group service-card bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden hover:border-cyan-500/50 animate-fade-in-delay-${index + 1}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="img-zoom h-48 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-zinc-500 mb-4">{service.description}</p>
                  <span className="text-cyan-400 font-semibold price-tag">ab {service.price}</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/services">
              <Button variant="outline" className="border-zinc-700 hover:border-cyan-500" data-testid="all-services-btn">
                Alle Services ansehen
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className={`text-center p-8 animate-fade-in-delay-${index + 1}`}
                data-testid={`benefit-${index}`}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-cyan-500/10 mb-4">
                  <benefit.icon className="h-6 w-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-zinc-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="membership-cta">
        <div className="max-w-4xl mx-auto">
          <div className="membership-premium rounded-sm p-8 md:p-12 bg-zinc-900">
            <div className="flex items-start gap-4 mb-6">
              <Crown className="h-8 w-8 text-cyan-400 flex-shrink-0" />
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 font-['Chivo']">
                  Premium-Mitglied werden
                </h2>
                <p className="text-zinc-400">
                  Genießen Sie exklusive Vorteile und sparen Sie bei jeder Buchung.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                "15% Rabatt auf alle Services",
                "Bevorzugte Terminbuchung",
                "Exklusive Angebote",
                "Kostenlose monatliche Auffrischung",
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm text-zinc-300">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="text-center sm:text-left">
                <span className="text-3xl font-bold text-white price-tag">7,99€</span>
                <span className="text-zinc-500 ml-2">/ Monat</span>
              </div>
              {isAuthenticated ? (
                isPremium ? (
                  <Button disabled className="bg-zinc-700 text-zinc-400">
                    Bereits Premium-Mitglied
                  </Button>
                ) : (
                  <Link to="/membership">
                    <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow" data-testid="upgrade-cta-btn">
                      Jetzt upgraden
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )
              ) : (
                <Link to="/register">
                  <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow" data-testid="register-cta-btn">
                    Kostenlos registrieren
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
